import { QueryClient, QueryFunction } from "@tanstack/react-query";

const SAFE_HTTP_METHODS = new Set(["GET", "HEAD", "OPTIONS", "TRACE"]);

/**
 * Generate a client-side request ID for tracing.
 */
function generateRequestId(): string {
  return `client_${crypto.randomUUID().slice(0, 16)}`;
}
const CSRF_COOKIE_HEADER_MAP: Array<{ cookie: string; header: string }> = [
  { cookie: "csrftoken", header: "X-CSRFToken" },
  { cookie: "csrfToken", header: "X-CSRFToken" },
  { cookie: "CSRF-TOKEN", header: "X-CSRFToken" },
  { cookie: "XSRF-TOKEN", header: "X-XSRF-TOKEN" },
  { cookie: "xsrf-token", header: "X-XSRF-TOKEN" },
];

interface RequestContext {
  method?: string;
  url?: string;
}

function buildRequestLabel(context?: RequestContext): string | null {
  if (!context) return null;
  const method = context.method?.toUpperCase();
  const url = context.url;
  if (method && url) {
    return `${method} ${url}`;
  }
  if (url) {
    return url;
  }
  if (method) {
    return method;
  }
  return null;
}

function buildCsrfErrorMessage(context?: RequestContext): string {
  const label = buildRequestLabel(context);
  const requestHint = label ? ` (${label})` : "";
  return [
    "The backend rejected the request with a CSRF verification error",
    "which usually means the dev proxy is hitting another service (often Django)",
    "on the /pyapi port. Stop that service or point PYAPI_TARGET at your FastAPI",
    "server and retry."
  ].join(" ") + requestHint;
}

function buildGenericErrorMessage(status: number, body: string, context?: RequestContext, requestId?: string): string {
  const label = buildRequestLabel(context);
  const prefix = label ? `${status} ${label}` : `${status}`;
  const sanitized = body.trim();

  // Try to parse JSON error response that includes requestId
  try {
    const parsed = JSON.parse(sanitized);
    if (parsed.requestId) {
      requestId = parsed.requestId;
    }
    // Use error message from parsed response if available
    const errorMsg = parsed.error || parsed.message || sanitized;
    const normalized = errorMsg.replace(/\s+/g, " ");
    const truncated = normalized.length > 300 ? `${normalized.slice(0, 297)}…` : normalized;
    const reqIdSuffix = requestId ? ` (Request ID: ${requestId})` : "";
    return `${prefix}: ${truncated}${reqIdSuffix}`;
  } catch {
    // Not JSON, use as-is
    if (!sanitized) {
      const reqIdSuffix = requestId ? ` (Request ID: ${requestId})` : "";
      return `${prefix}: Request failed without a response body.${reqIdSuffix}`;
    }
    const normalized = sanitized.replace(/\s+/g, " ");
    const truncated = normalized.length > 300 ? `${normalized.slice(0, 297)}…` : normalized;
    const reqIdSuffix = requestId ? ` (Request ID: ${requestId})` : "";
    return `${prefix}: ${truncated}${reqIdSuffix}`;
  }
}

async function throwIfResNotOk(res: Response, context?: RequestContext) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    const requestId = res.headers.get("X-Request-ID") || undefined;

    if (res.status === 403 && /CSRF verification failed/i.test(text)) {
      throw new Error(buildCsrfErrorMessage(context));
    }
    throw new Error(buildGenericErrorMessage(res.status, text, context, requestId));
  }
}

type ApiRequestOptions = {
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

function headerExists(headers: Record<string, string> | undefined, target: string): boolean {
  if (!headers) {
    return false;
  }
  const targetName = target.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === targetName);
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const cookies = document.cookie ? document.cookie.split(";") : [];
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.slice(name.length + 1));
    }
  }
  return null;
}

function resolveCsrfHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const { cookie, header } of CSRF_COOKIE_HEADER_MAP) {
    const value = getCookie(cookie);
    if (value && !headers[header]) {
      headers[header] = value;
    }
  }
  return headers;
}

function shouldAttachCsrf(method: string): boolean {
  return !SAFE_HTTP_METHODS.has(method.toUpperCase());
}

function buildRequestHeaders(
  method: string,
  includeJson: boolean,
  options?: ApiRequestOptions
): Record<string, string> {
  const headers: Record<string, string> = {};
  const userHeaders = options?.headers;

  // Add request ID for tracing (unless user provided one)
  if (!headerExists(userHeaders, "X-Request-ID")) {
    headers["X-Request-ID"] = generateRequestId();
  }

  if (includeJson && !headerExists(userHeaders, "Content-Type")) {
    headers["Content-Type"] = "application/json";
  }

  if (shouldAttachCsrf(method)) {
    const csrfHeaders = resolveCsrfHeaders();
    for (const [key, value] of Object.entries(csrfHeaders)) {
      if (value && !headerExists(userHeaders, key)) {
        headers[key] = value;
      }
    }
  }

  if (userHeaders) {
    for (const [key, value] of Object.entries(userHeaders)) {
      headers[key] = value;
    }
  }

  return headers;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: ApiRequestOptions
): Promise<Response> {
  const hasData = data !== undefined;
  const headers = buildRequestHeaders(method, hasData, options);

  const res = await fetch(url, {
    method,
    headers,
    body: hasData ? JSON.stringify(data) : undefined,
    credentials: "include",
    signal: options?.signal,
  });

  await throwIfResNotOk(res, { method, url });
  return res;
}

export async function apiRequestFormData(
  method: string,
  url: string,
  formData: FormData,
  options?: ApiRequestOptions
): Promise<Response> {
  const headers = buildRequestHeaders(method, false, options);

  const res = await fetch(url, {
    method,
    headers,
    body: formData,
    credentials: "include",
    signal: options?.signal,
  });

  await throwIfResNotOk(res, { method, url });
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const res = await fetch(url, {
      credentials: "include",
      headers: {
        "X-Request-ID": generateRequestId(),
      },
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res, { method: "GET", url });
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
